import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Paper, 
  Tooltip, 
  IconButton, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress
} from '@mui/material';
import { 
  Lock as LockIcon, 
  LockOpen as UnlockIcon, 
  Info as InfoIcon,
  ErrorOutline as WarningIcon
} from '@mui/icons-material';
import fileService, { LockStatusResponse } from '../../services/fileService';

interface FileLockProps {
  fileId: string;
  onLockChange?: (status: LockStatusResponse['lockStatus']) => void;
}

export const FileLock: React.FC<FileLockProps> = ({ fileId, onLockChange }) => {
  const [lockInfo, setLockInfo] = useState<LockStatusResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showInfoDialog, setShowInfoDialog] = useState<boolean>(false);

  const fetchLockStatus = async () => {
    try {
      setLoading(true);
      const response = await fileService.getFileLockStatus(fileId);
      setLockInfo(response);
    } catch (err) {
      setError('Failed to fetch lock status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLockStatus();

    // Poll for lock status updates every 30 seconds
    const interval = setInterval(fetchLockStatus, 30000);
    return () => clearInterval(interval);
  }, [fileId]);

  const handleLock = async () => {
    try {
      setLoading(true);
      await fileService.lockFile(fileId);
      await fetchLockStatus();
      if (onLockChange) onLockChange('locked');
    } catch (err) {
      setError('Failed to lock file');
    } finally {
      setLoading(false);
    }
  };

  const handleUnlock = async () => {
    try {
      setLoading(true);
      await fileService.unlockFile(fileId);
      await fetchLockStatus();
      if (onLockChange) onLockChange('unlocked');
    } catch (err) {
      setError('Failed to unlock file');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ color: 'error.main', p: 2 }}>
        <Typography variant="body2">{error}</Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {lockInfo?.lockStatus === 'locked' ? (
            <LockIcon color="warning" sx={{ mr: 1 }} />
          ) : (
            <UnlockIcon color="success" sx={{ mr: 1 }} />
          )}
          <Typography variant="subtitle1">
            {lockInfo?.lockStatus === 'locked' ? 'File is Locked' : 'File is Unlocked'}
          </Typography>
          <Tooltip title="About file locking">
            <IconButton size="small" onClick={() => setShowInfoDialog(true)}>
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        
        <Box>
          {lockInfo?.lockStatus === 'locked' ? (
            <Button 
              variant="outlined" 
              color="warning"
              startIcon={<UnlockIcon />} 
              onClick={handleUnlock}
              disabled={loading}
            >
              Unlock
            </Button>
          ) : (
            <Button 
              variant="outlined" 
              color="primary"
              startIcon={<LockIcon />} 
              onClick={handleLock}
              disabled={loading}
            >
              Lock
            </Button>
          )}
        </Box>
      </Box>
      
      {lockInfo?.lockStatus === 'locked' && lockInfo.lockedBy && (
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', bgcolor: 'warning.light', p: 1, borderRadius: 1 }}>
          <WarningIcon color="warning" fontSize="small" sx={{ mr: 1 }} />
          <Typography variant="body2" color="warning.dark">
            Locked by {lockInfo.lockedBy.name} at {formatDate(lockInfo.lockedAt)}
            {lockInfo.expiresAt && (
              <> (expires at {formatDate(lockInfo.expiresAt)})</>
            )}
          </Typography>
        </Box>
      )}

      {/* Information Dialog */}
      <Dialog open={showInfoDialog} onClose={() => setShowInfoDialog(false)}>
        <DialogTitle>About File Locking</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Typography paragraph>
              File locking prevents others from making changes to a file while you're working on it.
            </Typography>
            <Typography paragraph>
              <strong>When to use file locking:</strong>
            </Typography>
            <ul>
              <li>When you need to make extensive changes to a file</li>
              <li>When you want to prevent conflicting edits</li>
              <li>When working on critical documents</li>
            </ul>
            <Typography paragraph>
              Remember to unlock files when you're done working on them to allow others to make changes.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowInfoDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default FileLock; 